/**
 * ForEmployersCompliance — /for-employers/compliance
 *
 * Public marketing / trust page for prospective employers. Expands the
 * job-post trust badge into a full, linkable proof surface with
 * expandable FAQs. Zero authentication required so this page can be
 * shared in sales conversations, RFPs, and email signatures.
 *
 * Content is intentionally high-signal / low-jargon — plain-English
 * commitments backed by concrete workflows Simonara actually enforces
 * elsewhere in the product (Ban-the-Box gate, PII masking, FCRA
 * two-step adverse action, retention sweeps, deletion queue).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    ShieldCheck, ScaleIcon, CheckCircle2, FileCheck, Lock, Clock, Users,
    ChevronDown, ChevronUp, ArrowRight, ExternalLink, Scale,
} from "lucide-react";

const PILLARS = [
    {
        icon: Users,
        title: "Consent-first",
        body: "No background check runs on Simonara without the professional's written consent. A standalone disclosure is presented and signed through Checkr — separate from any employment agreement — before we send anything downstream.",
    },
    {
        icon: Scale,
        title: "Fair Chance Act",
        body: "You may only order a criminal-history check after extending a conditional offer of employment. Simonara requires the attestation on every US order and blocks the request at the API layer if it's missing — the workflow can't skip this step.",
    },
    {
        icon: FileCheck,
        title: "FCRA two-step adverse action",
        body: "If a report affects your decision, Simonara runs pre-notice, a 5-business-day dispute window, and final-notice for you. Candidates can dispute inaccuracies with Checkr directly; the platform tracks the clock so you don't have to.",
    },
    {
        icon: Lock,
        title: "Data minimization",
        body: "Employers and support staff see Clear / Consider results only. Sensitive identifiers (SSN, DOB, full report artifacts) are masked and gated behind a Simonara Compliance role — every access is audit-logged.",
    },
    {
        icon: Clock,
        title: "Retention with teeth",
        body: "Background-check records auto-purge 2 years after completion. Adverse-action rows are retained longer per FCRA record-keeping. A nightly preview email flags what will be scrubbed the next morning so nothing goes quietly.",
    },
    {
        icon: ShieldCheck,
        title: "Checkr-powered",
        body: "Every check runs through Checkr, an FCRA-compliant US Consumer Reporting Agency. Simonara owns the compliance workflow around it — orchestration, gating, and audit — but the CRA itself is a regulated third party subject to independent oversight.",
    },
];

const FAQS = [
    {
        q: "Can I skip the conditional-offer step to move faster?",
        a: "No — and the platform won't let you. The 'Request Employment Screening' API rejects any order without the Fair Chance Act attestation, and every UI trigger surfaces the requirement before the dialog opens. This is a hard gate, not a suggestion.",
    },
    {
        q: "What exactly do recruiters see on a report?",
        a: "Only the final aggregated result — 'Clear' or 'Consider' — plus the package that was run and the completion date. Recruiters do not see raw report content, SSN, DOB, address, Checkr adjudication data, or the candidate's signature. Those live in a Compliance role behind a separate consent-based unmask flow, and every unmask is audit-logged.",
    },
    {
        q: "How does the adverse-action workflow work in practice?",
        a: "If a Consider result influences a decision, Simonara Compliance kicks off the two-step FCRA notice: (1) pre-adverse notice with a copy of the report and a summary of the candidate's rights, (2) a 5-business-day waiting window during which the candidate can dispute inaccuracies with Checkr, and (3) Checkr auto-schedules the final adverse-action notice after the window closes. You don't call any endpoints — you just wait for Compliance to close the loop.",
    },
    {
        q: "What if a candidate disputes their report?",
        a: "Dispute goes straight into a Compliance queue with the original data, the candidate's stated inaccuracy, and any supporting details. Checkr re-investigates within 30 days per FCRA § 611. The platform blocks any final adverse-action notice while a dispute is open.",
    },
    {
        q: "How long do you keep the data?",
        a: "Records auto-purge 2 years after the check completes. Adverse-action records are retained longer per FCRA record-keeping rules (typically 5 years). Candidates can request earlier deletion from their Talent dashboard; Compliance reviews and approves each request, applying a legal hold when required (e.g. an active dispute).",
    },
    {
        q: "Can candidates get a copy of the report?",
        a: "Yes — for free, at any time, straight from their Talent dashboard at /professional/background-checks. They can also download a PDF of the exact disclosure and authorization they signed at consent time. This is not gated behind an adverse decision; it's a baseline right.",
    },
    {
        q: "What about candidates in California, New York City, or other 'ban-the-box' jurisdictions?",
        a: "The conditional-offer requirement is enforced globally on our platform — California's is the strictest rule so we apply it everywhere. Jurisdiction-specific disclosure templates are stored and versioned, and the exact template shown to the candidate at signing time is snapshotted with the authorization record so a future template change never rewrites signed history.",
    },
    {
        q: "How do you handle deletion requests from candidates?",
        a: "Deletion requests flow into a Compliance queue at /admin/background-checks/retention. Compliance approves (immediate scrub of non-held rows) or rejects with a note. Any row under active adverse action, open dispute, or legal hold is automatically exempted — the sweep skips it and tags the reason in the audit log.",
    },
    {
        q: "Do you share any candidate data with third parties beyond Checkr?",
        a: "The full subprocessor list is public at /subprocessors. For background checks specifically, only Checkr receives candidate identifiers (name, DOB, address, SSN where the package requires it) — and only after the candidate has signed the disclosure. No data is sold, and no data is shared with recruiting-intel or advertising vendors.",
    },
    {
        q: "Can I require a candidate to buy a Simonara Snapshot before I interview or hire them?",
        a: "No. The Simonara Snapshot is a candidate-owned, voluntary product. Employers may not condition an application, an interview, an offer, or a hire on the candidate purchasing a Snapshot — full stop. If you want a background check as part of a hiring decision, use the employer-initiated employment screening flow, which requires a conditional offer under the CA Fair Chance Act and follows the FCRA two-step adverse-action process. Snapshots exist so candidates can proactively self-verify for their own records; they are never a hiring gate.",
    },
    {
        q: "Where can I read the underlying policies?",
        a: "The full consent policy lives at /verification-consent-policy. The wider Trust Center is at /trust-center. Our Privacy Policy is at /privacy, and Data Processing Agreement lives at /dpa. For a specific compliance question you don't see here, email privacy@simonaratalent.us — we typically reply within one business day.",
    },
];

const Pillar = ({ icon: Icon, title, body, testId }) => (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testId}>
        <div className="inline-flex items-center gap-2 text-foreground">
            <Icon size={16} className="text-emerald-600" />
            <h3 className="font-heading font-semibold text-base">{title}</h3>
        </div>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
    </div>
);

const FaqItem = ({ faq, idx }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-border last:border-b-0" data-testid={`fec-faq-${idx}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full text-left py-4 flex items-start justify-between gap-4 group"
                data-testid={`fec-faq-toggle-${idx}`}
                aria-expanded={open}
            >
                <span className="font-semibold text-foreground group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">
                    {faq.q}
                </span>
                <span className="mt-0.5 shrink-0 text-muted-foreground">
                    {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </span>
            </button>
            {open && (
                <p className="pb-5 text-sm text-muted-foreground leading-relaxed" data-testid={`fec-faq-answer-${idx}`}>
                    {faq.a}
                </p>
            )}
        </div>
    );
};

export default function ForEmployersCompliance() {
    return (
        <div className="pb-16" data-testid="for-employers-compliance-page">
            {/* Hero */}
            <header className="border-b border-border bg-gradient-to-b from-emerald-500/[0.04] via-transparent to-transparent">
                <div className="container-app py-14 md:py-20">
                    <div className="max-w-3xl">
                        <p className="overline inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                            <ShieldCheck size={12} /> Compliance for hiring teams
                        </p>
                        <h1 className="mt-3 font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground">
                            Hire with confidence.
                            <br />
                            <span className="text-emerald-700 dark:text-emerald-400">Compliance is built in.</span>
                        </h1>
                        <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                            Simonara runs background checks through <strong className="text-foreground">Checkr</strong>,
                            an FCRA-compliant US Consumer Reporting Agency, and owns the compliance workflow around it:
                            consent-first orders, Fair Chance Act gating, FCRA two-step adverse action, and audit-logged
                            data minimization. This page is the linkable proof.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-2">
                            <Link
                                to="/employers"
                                className="inline-flex items-center gap-1.5 rounded-full bg-foreground text-background px-5 py-2.5 text-sm font-semibold hover:opacity-90"
                                data-testid="fec-cta-employers"
                            >
                                Start hiring on Simonara <ArrowRight size={13} />
                            </Link>
                            <a
                                href="mailto:privacy@simonaratalent.us?subject=Compliance%20question"
                                className="inline-flex items-center gap-1.5 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-muted"
                                data-testid="fec-cta-contact"
                            >
                                Ask a compliance question
                            </a>
                        </div>
                    </div>
                </div>
            </header>

            {/* Pillars */}
            <section className="container-app py-12" data-testid="fec-pillars">
                <div className="max-w-2xl">
                    <p className="overline">The commitments</p>
                    <h2 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">
                        Six commitments, enforced in code — not just written down.
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Each of these has a specific workflow behind it. Where it's not obvious how, the FAQ below
                        spells it out.
                    </p>
                </div>
                <div className="mt-7 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {PILLARS.map((p, i) => (
                        <Pillar key={p.title} icon={p.icon} title={p.title} body={p.body} testId={`fec-pillar-${i}`} />
                    ))}
                </div>
            </section>

            {/* Attestation bar */}
            <section className="border-y border-border bg-muted/30">
                <div className="container-app py-8">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-2 text-foreground" data-testid="fec-attestation">
                            <ScaleIcon size={16} className="text-emerald-600" />
                            <span className="font-semibold">
                                FCRA + California Fair Chance Act compliant · Checkr powered
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm">
                            <Link to="/trust-center" className="inline-flex items-center gap-1 text-foreground hover:underline" data-testid="fec-link-trust">
                                Trust Center <ExternalLink size={11} />
                            </Link>
                            <Link to="/verification-consent-policy" className="inline-flex items-center gap-1 text-foreground hover:underline" data-testid="fec-link-vcp">
                                Consent Policy <ExternalLink size={11} />
                            </Link>
                            <Link to="/subprocessors" className="inline-flex items-center gap-1 text-foreground hover:underline" data-testid="fec-link-subprocessors">
                                Subprocessors <ExternalLink size={11} />
                            </Link>
                            <Link to="/dpa" className="inline-flex items-center gap-1 text-foreground hover:underline" data-testid="fec-link-dpa">
                                DPA <ExternalLink size={11} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQs */}
            <section className="container-app py-14" data-testid="fec-faqs">
                <div className="max-w-2xl">
                    <p className="overline">FAQ</p>
                    <h2 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">
                        The questions procurement teams actually ask.
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        If yours isn't here, email{" "}
                        <a href="mailto:privacy@simonaratalent.us" className="underline text-foreground">privacy@simonaratalent.us</a>{" "}
                        — we typically reply within one business day.
                    </p>
                </div>
                <div className="mt-7 rounded-2xl border border-border bg-card p-2 md:p-4">
                    {FAQS.map((faq, i) => (
                        <FaqItem key={i} faq={faq} idx={i} />
                    ))}
                </div>
            </section>

            {/* Close */}
            <section className="container-app pb-12">
                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.04] p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="max-w-xl">
                        <h3 className="font-heading font-semibold text-xl text-foreground">
                            Ready to move faster without cutting corners?
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Post your first role in under 5 minutes. Every downstream background check inherits the
                            controls on this page.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Link
                            to="/employers"
                            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 text-sm font-semibold"
                            data-testid="fec-close-cta"
                        >
                            Start on the Employer track <ArrowRight size={13} />
                        </Link>
                        <Link
                            to="/enterprise"
                            className="inline-flex items-center gap-1.5 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-muted"
                            data-testid="fec-close-cta-enterprise"
                        >
                            Talk to Enterprise
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
